import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel,
} from '@microsoft/signalr';
import { appConfig } from '../config';
import { getCsrfToken } from '../api/client';
import type {
  BackendDeviceStatus,
  ConnectionState,
  DeviceMetrics,
  NetworkTelemetry,
} from '../types';
import type { BackendDevice } from '../api/devices';

export type MetricsUpdate = DeviceMetrics;
export type NetworkTelemetryUpdate = NetworkTelemetry;

export interface MonitoringHubHandlers {
  onConnectionState: (state: ConnectionState) => void;
  onDeviceCreated: (device: BackendDevice) => void;
  onDeviceUpdated: (device: BackendDevice) => void;
  onDeviceDeleted: (deviceId: string) => void;
  onDeviceStatus: (deviceId: string, status: BackendDeviceStatus) => void;
  onMetrics: (deviceId: string, metrics: MetricsUpdate) => void;
  onNetworkTelemetry: (deviceId: string, metrics: NetworkTelemetryUpdate) => void;
}

export class MonitoringHubClient {
  private connection: HubConnection | null = null;

  constructor(private readonly handlers: MonitoringHubHandlers) {}

  async connect() {
    if (this.connection && this.connection.state !== HubConnectionState.Disconnected) return;

    this.handlers.onConnectionState('connecting');

    const csrfToken = getCsrfToken();

    this.connection = new HubConnectionBuilder()
      .withUrl(appConfig.signalRUrl, {
        transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
        withCredentials: true,
        headers: csrfToken ? { 'X-KorsaApp-CSRF': csrfToken } : {},
      })
      .configureLogging(import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error)
      .withAutomaticReconnect([0, 2_000, 10_000, 30_000])
      .build();

    this.connection.on('DeviceCreated', this.handlers.onDeviceCreated);
    this.connection.on('DeviceUpdated', this.handlers.onDeviceUpdated);
    this.connection.on('DeviceDeleted', this.handlers.onDeviceDeleted);
    this.connection.on('DeviceStatusChanged', this.handlers.onDeviceStatus);
    this.connection.on('MetricsUpdated', this.handlers.onMetrics);
    this.connection.on('NetworkTelemetryUpdated', this.handlers.onNetworkTelemetry);
    this.connection.on('EventCreated', () => { window.dispatchEvent(new Event('korsaapp:events-changed')); });
    this.connection.on('EventChanged', () => { window.dispatchEvent(new Event('korsaapp:events-changed')); });
    this.connection.onreconnecting(() => this.handlers.onConnectionState('reconnecting'));
    this.connection.onreconnected(() => {
      this.handlers.onConnectionState('connected');
      window.dispatchEvent(new Event('korsaapp:events-changed'));
    });
    this.connection.onclose(() => this.handlers.onConnectionState('disconnected'));

    try {
      await this.connection.start();
      this.handlers.onConnectionState('connected');
    } catch {
      this.handlers.onConnectionState('disconnected');
    }
  }

  async disconnect() {
    if (!this.connection) return;
    await this.connection.stop();
    this.connection = null;
    this.handlers.onConnectionState('disconnected');
  }
}
